import { packageActionTypes as actionTypes } from '../../ActionTypes/Voss/packageActionTypes';
import packageActions from './packageActions';
import { apiThunk } from '../../Utils/apiThunk';
import { mutatePackageResource, packageResourceUrl } from './packageResourceApi';
import { toPackageDraft, toUpdatePackageRequest } from '../../Components/Voss/Packages/packageDraftUtils';
import type { AppDispatch } from '../../Store/types';
import type { VossVersionStatus } from '../../Constants/VOSS/vossVersionStatus';
import type {
    CreatePackageRequest,
    CreatePackageSalesChannelRequest,
    CreatePackageVersionRequest,
    PackageDetail,
    PackageSalesChannel,
    SalesChannel,
    UpdatePackageRequest,
    UpdatePackageSalesChannelRequest,
} from '../../Components/Voss/Packages/Packages.types';

const salesChannelsUrl = (packageId: string, salesChannelId?: string) => packageResourceUrl(packageId, 'salesChannels', undefined, salesChannelId);

// resolves with the saved package, or null when the write failed (the toast has already been shown)
const updatePackage = (id: string, model: UpdatePackageRequest, versionId: string | undefined) => {
    const query = versionId === undefined || versionId.length === 0 ? '' : `?versionId=${versionId}`;

    return (dispatch: AppDispatch) =>
        dispatch(apiThunk<PackageDetail>(`/api/packages/${id}${query}`, { init: { method: 'PUT', body: JSON.stringify(model) } })).then((data) => {
            if (data === null) return null;

            dispatch({ type: actionTypes.STORE_PACKAGE, data });
            void dispatch(packageActions.getPackageVersions(id));
            // the list refetches itself on next visit, so drop it rather than spending a call here
            void dispatch(packageActions.clear('packages'));

            return data;
        });
};

const packageUpdateActions = {
    createPackage(model: CreatePackageRequest) {
        return (dispatch: AppDispatch) =>
            dispatch(apiThunk<PackageDetail>('/api/packages/create', { init: { method: 'POST', body: JSON.stringify(model) } })).then((data) => {
                if (data === null) return null;

                void dispatch(packageActions.clear('packages'));

                return data;
            });
    },

    updatePackage,

    changePackageStatus(id: string, vossPackage: PackageDetail, status: VossVersionStatus) {
        return updatePackage(id, { ...toUpdatePackageRequest(toPackageDraft(vossPackage), vossPackage), status }, vossPackage.versionId);
    },

    // the container navigates to the new version, which fetches it; storing it here would show it under the old versionId first
    createPackageVersion(id: string, model: CreatePackageVersionRequest) {
        return (dispatch: AppDispatch) =>
            dispatch(apiThunk<PackageDetail>(packageResourceUrl(id, 'versions', undefined), { init: { method: 'POST', body: JSON.stringify(model) } })).then((data) => {
                if (data === null) return null;

                void dispatch(packageActions.getPackageVersions(id));
                void dispatch(packageActions.clear('packages'));

                return data;
            });
    },

    getPackageSalesChannels(packageId: string, signal?: AbortSignal) {
        return (dispatch: AppDispatch) =>
            dispatch(apiThunk<PackageSalesChannel[]>(salesChannelsUrl(packageId), { loadingKey: 'packageSalesChannels', init: { signal } })).then((data) => {
                if (signal?.aborted !== true) dispatch({ type: actionTypes.STORE_PACKAGE_SALES_CHANNELS, data });
            });
    },

    getSalesChannelCatalog() {
        return (dispatch: AppDispatch) =>
            dispatch(apiThunk<SalesChannel[]>('/api/salesChannels/GetSalesChannels', { loadingKey: 'salesChannelCatalog' })).then((data) => {
                dispatch({ type: actionTypes.STORE_SALES_CHANNEL_CATALOG, data });
            });
    },

    addPackageSalesChannel(packageId: string, model: CreatePackageSalesChannelRequest) {
        return mutatePackageResource(salesChannelsUrl(packageId), { method: 'POST', body: JSON.stringify(model) });
    },

    updatePackageSalesChannel(packageId: string, salesChannelId: string, model: UpdatePackageSalesChannelRequest) {
        return mutatePackageResource(salesChannelsUrl(packageId, salesChannelId), { method: 'PUT', body: JSON.stringify(model) });
    },

    removePackageSalesChannel(packageId: string, salesChannelId: string) {
        return mutatePackageResource(salesChannelsUrl(packageId, salesChannelId), { method: 'DELETE' });
    },
};

export default packageUpdateActions;
